import { useRef, type RefObject } from 'react';
import { Link } from 'react-router';
import {
  Avatar,
  AvatarFallback,
  Button,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import {
  CopyIcon,
  EyeIcon,
  GlobeIcon,
  LayoutTemplateIcon,
  LockIcon,
  MoreVerticalIcon,
  PencilIcon,
  Share2Icon,
  Trash2Icon,
  UsersIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TrackerIconTile, type Tracker } from '@/entities/tracker';
import { useAuth, useUserDisplay } from '@/entities/user';
import { ROUTES } from '@/shared/config/routes';
import { useModalState } from '@/shared/hooks';
import { useTrackerAccess } from '../../hooks/useTrackerAccess';
import { TrackerShareModal } from '../../TrackerShareButton';
import { SaveTrackerTemplateDialog } from '../SaveTrackerTemplateDialog';

const SHARING_ICON = { private: LockIcon, shared: UsersIcon, workspace: GlobeIcon } as const;

function CreatorLine({ createdBy }: { createdBy: string }) {
  const { t } = useTranslation('trackers');
  const currentUserId = useAuth((s) => s.signedInUser.value)?.id ?? null;
  const isMine = currentUserId !== null && createdBy === currentUserId;
  const creator = useUserDisplay(createdBy);

  const pending = !isMine && creator.isLoading;
  const label = isMine
    ? t('trackers.trackerCard.createdByYou')
    : creator.name
      ? t('trackers.trackerCard.sharedBy', { name: creator.name })
      : t('trackers.trackerCard.sharedByTeammate');

  return (
    <span className="flex min-w-0 items-center gap-xs text-foreground">
      <Avatar aria-hidden="true" className="size-6 shrink-0">
        <AvatarFallback className="bg-accent text-label-sm font-medium text-accent-foreground uppercase">
          {creator.initials ?? '?'}
        </AvatarFallback>
      </Avatar>
      {pending ? (
        <Skeleton className="h-4 w-24" data-testid="tracker-card-creator-pending" />
      ) : (
        <span className="truncate">{label}</span>
      )}
    </span>
  );
}

// Mirrors workspace-api's deriveTrackerSharing, which still fills the deprecated `sharing` field.
function sharingOf(tracker: Tracker): keyof typeof SHARING_ICON {
  if (tracker.visibility === 'public') return 'workspace';
  return tracker.sharedWithCount > 0 ? 'shared' : 'private';
}

function SharingHint({ tracker }: { tracker: Tracker }) {
  const { t } = useTranslation('trackers');
  const currentUserId = useAuth((s) => s.signedInUser.value)?.id ?? null;
  const isMine = tracker.createdBy === currentUserId;
  const { canEdit, canManage } = useTrackerAccess(tracker);
  const sharing = sharingOf(tracker);
  // Someone else's tracker shared with you says what you can do on it. An admin
  // manages every tracker, so theirs reads like the owner's.
  const sharedWithMe = sharing === 'shared' && !canManage;
  const viewOnly = !canEdit;
  const Icon = sharedWithMe ? (viewOnly ? EyeIcon : PencilIcon) : SHARING_ICON[sharing];
  const label = sharedWithMe
    ? viewOnly
      ? t('trackers.trackerCard.canView')
      : t('trackers.trackerCard.canEdit')
    : sharing === 'shared'
      ? t('trackers.trackerCard.sharedWith', { count: tracker.sharedWithCount })
      : sharing === 'private'
        ? isMine
          ? t('trackers.trackerCard.onlyYou')
          : t('trackers.trackerCard.private')
        : t('trackers.trackerCard.everyoneInOrganization');

  return (
    <span className="flex shrink-0 items-center gap-xs text-muted-foreground">
      <Icon className="size-3.5" aria-hidden="true" />
      {label}
    </span>
  );
}

export function TrackerCard({
  tracker,
  onEdit,
  onDelete,
  onDuplicate,
}: {
  tracker: Tracker;
  /** Hands over the menu trigger, which is where focus belongs once the edit closes. */
  onEdit: (id: string, menuTrigger: RefObject<HTMLButtonElement | null>) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
}) {
  const { t } = useTranslation('trackers');
  const { id, name, description } = tracker;
  const { canManage } = useTrackerAccess(tracker);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const shareModal = useModalState();
  const saveDialog = useModalState();

  return (
    <Card
      data-testid="tracker-card"
      className="group relative h-full gap-0 rounded-lg border border-border-light bg-transparent p-0 text-card-foreground shadow-none transition-colors hover:border-border focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
    >
      {/* Actions menu: a sibling of the Link (not a descendant), so opening it
         never triggers the card's navigation. */}
      <div className="absolute right-4 top-4 z-10">
        <TooltipProvider>
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    ref={menuTriggerRef}
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t('trackers.trackerActionsFor', { name })}
                    className="text-muted-foreground"
                  >
                    <MoreVerticalIcon className="size-4" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>{t('trackers.trackerActionsFor', { name })}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end">
              {/* Your own trackers are managed from here; one shared with you
                    can only be copied into one of your own. */}
              {canManage ? (
                <>
                  <DropdownMenuItem onSelect={() => onEdit(id, menuTriggerRef)}>
                    <PencilIcon className="size-4" aria-hidden />
                    {t('trackers.trackerDetailsMenu.editDetails')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={shareModal.open}>
                    <Share2Icon className="size-4" aria-hidden />
                    {t('trackers.trackerShareButton.share')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={saveDialog.open}>
                    <LayoutTemplateIcon className="size-4" aria-hidden />
                    {t('trackers.trackerDetailsMenu.saveAsTemplate')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => onDelete(id)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2Icon className="size-4" aria-hidden />
                    {t('trackers.delete')}
                  </DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem onSelect={() => onDuplicate(id)}>
                  <CopyIcon className="size-4" aria-hidden />
                  {t('trackers.duplicate')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </TooltipProvider>
      </div>

      <Link
        to={ROUTES.TRACKER_DETAIL(id)}
        className="flex h-full flex-col gap-xl rounded-lg p-section no-underline outline-none"
      >
        <TrackerIconTile
          icon={tracker.icon}
          hue={tracker.hue}
          size="lg"
          data-testid="tracker-card-icon"
        />

        <div className="flex flex-1 flex-col gap-sm">
          <h3 className="type-heading-md text-foreground">{name}</h3>
          <p className="line-clamp-4 type-body-md text-pretty text-foreground-alt">
            {description ?? t('trackers.customTracker')}
          </p>
        </div>

        <div className="flex items-center justify-between gap-sm border-t border-border-light pt-md type-caption">
          {tracker.defaultKey ? (
            <span className="truncate text-foreground">
              {t('trackers.trackerCard.organizationDefault')}
            </span>
          ) : (
            <CreatorLine createdBy={tracker.createdBy} />
          )}
          <SharingHint tracker={tracker} />
        </div>
      </Link>

      {shareModal.isMounted ? (
        <TrackerShareModal
          key={shareModal.instanceKey}
          open={shareModal.isOpen}
          onClose={shareModal.close}
          tracker={tracker}
        />
      ) : null}
      {saveDialog.isMounted ? (
        <SaveTrackerTemplateDialog
          key={saveDialog.instanceKey}
          tracker={tracker}
          open={saveDialog.isOpen}
          onClose={saveDialog.close}
        />
      ) : null}
    </Card>
  );
}
